import { useEffect, useState } from "react";

type Segment = { text: string; className?: string };

type Props = {
  text: string | Segment[];
  speed?: number;
  startDelay?: number;
  className?: string;
};

function Caret({ done }: { done: boolean }) {
  return (
    <span
      aria-hidden
      className={`inline-block w-[0.08em] h-[0.9em] align-[-0.1em] ml-1 bg-accent ${
        done ? "animate-pulse" : ""
      }`}
      style={{ animation: done ? "caret-blink 1s steps(1) infinite" : undefined }}
    />
  );
}

export function TypingText({ text, speed = 35, startDelay = 200, className = "" }: Props) {
  const fullText = typeof text === "string" ? text : text.map((s) => s.text).join("");
  const [count, setCount] = useState(0);

  useEffect(() => {
    const prefersReduced =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) {
      setCount(fullText.length);
      return;
    }
    setCount(0);
    const start = window.setTimeout(() => {
      const id = window.setInterval(() => {
        setCount((c) => {
          if (c >= fullText.length) {
            window.clearInterval(id);
            return c;
          }
          return c + 1;
        });
      }, speed);
    }, startDelay);
    return () => window.clearTimeout(start);
  }, [fullText, speed, startDelay]);

  const done = count >= fullText.length;

  if (typeof text === "string") {
    return (
      <span className={className}>
        {text.slice(0, count)}
        <Caret done={done} />
      </span>
    );
  }

  let processed = 0;
  return (
    <span className={className}>
      {text.map((segment, i) => {
        const before = processed;
        const after = before + segment.text.length;
        const visibleLength = Math.max(0, Math.min(count - before, segment.text.length));
        const visible = segment.text.slice(0, visibleLength);
        processed = after;
        const isCurrent = count > before && count <= after;
        return (
          <span key={i} className={segment.className}>
            {visible}
            {isCurrent && <Caret done={done} />}
          </span>
        );
      })}
    </span>
  );
}
